import React, {useState} from 'react';
import Modal from 'react-modal';
import styles from './CreateWorkerRequestPopUp.module.css';
import Button from '../../../components/common/button/Button';
import Calendar from '../../../components/common/calendar/Calendar';
import {CreateWorkerRequest} from "../../../types/client";
import {createWorkerRequestAPI} from "../../../services/client";
import {useAlert} from "../../../components/common/error/AlertContext";

interface CreateWorkerRequestPopUpProps {
    isOpen: boolean;
    onClose: () => void;
}

// 컴포넌트 내부용 타입 (Date 객체 사용)
interface InternalCreateWorkerRequest {
    startedAt: Date;
    workerAmount: number;
    note: string;
}

//작업자 추가 요청 생성 팝업
const CreateWorkerRequestPopUp: React.FC<CreateWorkerRequestPopUpProps> = ({
                                                                               isOpen,
                                                                               onClose
                                                                           }) => {
    // 내부적으로는 Date 객체로 관리
    const [requestData, setRequestData] = useState<InternalCreateWorkerRequest>({
        startedAt: new Date(),
        workerAmount: 0,
        note: ""
    });

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showCalendar, setShowCalendar] = useState<boolean>(false);
    const {showAlert} = useAlert();

    // Date 객체를 YYYY-MM-DD 문자열로 변환
    //날짜 데이터 형식 변환, 없으면 '-', 있으면 시간 데이터 제외
    const formatDateToString = (date: Date | string): string => {
        if (!date) return '';
        const dateObj = date instanceof Date ? date : new Date(date);

        if (isNaN(dateObj.getTime())) {
            return '-';
        }
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // API 전송용 데이터 변환
    const getApiRequestData = (): CreateWorkerRequest => ({
        startedAt: formatDateToString(requestData.startedAt),
        workerAmount: requestData.workerAmount,
        note: requestData.note
    });

    // 요청 생성
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (isLoading) return;
        if (!requestData.workerAmount) {
            //작업자 수 입력 필수
            showAlert('작업자 수를 입력해주세요.', "error");
            return;
        }
        setIsLoading(true);
        try {
            // Date 객체를 문자열로 변환하여 전송
            const apiData = getApiRequestData();

            // 요청 생성 API
            await createWorkerRequestAPI(apiData);
            setRequestData({
                startedAt: new Date(),
                workerAmount: 0,
                note: ""
            });
            onClose();
        } catch (error) {
            console.error('Worker request failed:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            className={styles.modal}
            overlayClassName={styles.overlay}
        >
            <div className={styles.modalContent}>
                <div className={styles.header}>
                    <h2>작업자 추가 요청</h2>
                    {/*닫기 버튼*/}
                    <button
                        onClick={onClose}
                        className={styles.closeButton}
                        type="button"
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>
                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.formGroup}>
                        <label>추가 작업자 수</label>
                        {/*추가할 작업자 수 선택, 양수만 가능*/}
                        <input
                            type="number"
                            value={requestData.workerAmount === 0 ? '' : requestData.workerAmount}
                            onChange={(e) => setRequestData(prev => ({
                                ...prev,
                                workerAmount: parseInt(e.target.value) || 0
                            }))}
                            min="1"
                            className={styles.input}
                            disabled={isLoading}
                        />
                    </div>

                    <div className={styles.formGroup}>
                    </div>

                    <div className={styles.formGroup}>
                        <label>희망 시작일</label>
                        {/*희망 시작일 선택*/}
                        <div className={styles.datePickerWrapper}>
                            <div
                                className={styles.dateInput}
                                onClick={() => setShowCalendar(!showCalendar)}
                            >
                                {requestData.startedAt.toLocaleDateString('ko-KR', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}
                            </div>
                            {showCalendar && (
                                <div className={styles.calendarPopup}>
                                    {/*Calendar 컴포넌트 사용하여 선택*/}
                                    <Calendar
                                        selectedDate={requestData.startedAt}
                                        onDateSelect={(date) => {
                                            setRequestData(prev => ({
                                                ...prev,
                                                startedAt: date
                                            }));
                                            setShowCalendar(false);
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={styles.formGroup}>
                        <label>비고란</label>
                        {/*비고 입력, 텍스트홀더 내용 수정 필요*/}
                        <textarea
                            value={requestData.note}
                            onChange={(e) => setRequestData(prev => ({
                                ...prev,
                                note: e.target.value || ""
                            }))}
                            placeholder="비고"
                            className={styles.textarea}
                        />
                    </div>
                    {/*주의 문구*/}
                    <p className={styles.notice}>
                        추가된 작업자는 이후 축소 불가능함에 유의해주세요
                    </p>
                    {/*요청 버튼*/}
                    <Button
                        type="submit"
                        variant="primary"
                        size="large"
                        fullWidth
                        disabled={isLoading}
                    >
                        요청
                    </Button>
                </form>
            </div>
        </Modal>
    );
};

export default CreateWorkerRequestPopUp;